import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Menu, X, Download } from 'lucide-react';

export default function Header() {
  const { t, language, setLanguage, LANGUAGES } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentLang = LANGUAGES.find(l => l.code === language);

  const navLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#categories', label: t('nav.categories') },
    { href: '#how-it-works', label: t('nav.howItWorks') },
    { href: '#faq', label: t('nav.faq') },
  ];

  return (
    <header className="glass sticky top-0 z-50" data-testid="header">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_4f732c3a-34ed-4f43-939a-46403ae9e699/artifacts/pnmsau6a_Background.png"
              alt="Ascifiw3 Network"
              className="h-10 w-10 rounded-full object-cover"
            />
            <span className="font-bold text-lg tracking-tight hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ascifiw3
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/privacy"
              className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid="nav-link-privacy"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid="nav-link-terms"
            >
              Terms
            </Link>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-sm font-medium"
                  data-testid="language-switcher-trigger"
                >
                  <span className="text-lg leading-none">{currentLang?.flag}</span>
                  <span className="hidden sm:inline uppercase">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" data-testid="language-dropdown">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`gap-3 cursor-pointer ${language === lang.code ? 'bg-zinc-100 font-semibold' : ''}`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Download CTA */}
            <a href="#get-the-app" className="hidden md:block">
              <Button
                className="rounded-full bg-[#008751] text-white hover:bg-[#006b40] flex items-center gap-2 transition-transform hover:-translate-y-0.5 hover:shadow-lg"
                data-testid="header-download-btn"
              >
                <Download className="w-4 h-4" />
                {t('nav.download')}
              </Button>
            </a>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-zinc-100 pb-6 pt-4" data-testid="mobile-nav">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-medium text-zinc-600 hover:text-zinc-900 py-2 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/privacy"
                onClick={() => setMobileOpen(false)}
                className="text-base font-medium text-zinc-600 hover:text-zinc-900 py-2 transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                onClick={() => setMobileOpen(false)}
                className="text-base font-medium text-zinc-600 hover:text-zinc-900 py-2 transition-colors"
              >
                Terms
              </Link>
              <a href="#get-the-app">
                <Button
                  className="rounded-full bg-[#008751] text-white hover:bg-[#006b40] mt-3 flex items-center gap-2"
                  data-testid="mobile-download-btn"
                >
                  <Download className="w-4 h-4" />
                  {t('nav.download')}
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
